import React, { useEffect, useState } from 'react';
import { FaCog, FaSave } from 'react-icons/fa';
import Header from '../Header';
import api from '../services/apiService';
import { applyTeamSettingsToSession, DEFAULT_KIT_COLOUR } from '../utils/teamSettings';
import '../scss/admin/TeamSettings.scss';

const TeamSettings = () => {
  const teamId = sessionStorage.getItem('selected_team_id');
  const [hideLeaderboard, setHideLeaderboard] = useState(false);
  const [hideWinsDrawsLosses, setHideWinsDrawsLosses] = useState(false);
  const [hidePlayerRatings, setHidePlayerRatings] = useState(false);
  const [kitColour, setKitColour] = useState(DEFAULT_KIT_COLOUR);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!teamId) {
        setError('No team selected');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const response = await api.get(
          `${process.env.REACT_APP_API_URL}/get_team_settings.php?team_id=${teamId}`
        );
        if (!response.data.success) {
          throw new Error(response.data.message || 'Could not load settings');
        }
        const settings = response.data.settings || {};
        setHideLeaderboard(!!settings.hide_leaderboard);
        setHideWinsDrawsLosses(!!settings.hide_wins_draws_losses);
        setHidePlayerRatings(!!settings.hide_player_ratings);
        setKitColour(settings.kit_colour || DEFAULT_KIT_COLOUR);
      } catch (err) {
        console.error(err);
        setError(err.message || 'Could not load settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [teamId]);

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!teamId) {
      setError('No team selected');
      return;
    }
    if (kitColour && !/^#[0-9A-Fa-f]{6}$/.test(kitColour)) {
      setError('Shirt colour must be a hex value like #9f1717');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        team_id: parseInt(teamId, 10),
        hide_leaderboard: hideLeaderboard,
        hide_wins_draws_losses: hideWinsDrawsLosses,
        hide_player_ratings: hidePlayerRatings,
        kit_colour: kitColour,
      };
      const response = await api.post(`${process.env.REACT_APP_API_URL}/update_team_settings.php`, payload);
      if (!response.data.success) {
        throw new Error(response.data.message || 'Save failed');
      }
      applyTeamSettingsToSession(response.data.settings || payload);
      setSuccess('Settings saved');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="team-settings container">
      <Header />
      <h1>
        <FaCog /> Team Settings
      </h1>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {loading ? (
        <p>Loading…</p>
      ) : (
        <form className="settings-form" onSubmit={handleSave}>
          <fieldset className="settings-block">
            <legend>Leaderboard</legend>
            <label className="toggle-row">
              <input
                type="checkbox"
                checked={hideLeaderboard}
                onChange={(e) => setHideLeaderboard(e.target.checked)}
              />
              <span>
                <strong>Hide Leaderboard</strong>
                <small>Removes Leaderboard from navigation for everyone on this team</small>
              </span>
            </label>
          </fieldset>

          <fieldset className="settings-block">
            <legend>Player profile</legend>
            <label className="toggle-row">
              <input
                type="checkbox"
                checked={hideWinsDrawsLosses}
                onChange={(e) => setHideWinsDrawsLosses(e.target.checked)}
              />
              <span>
                <strong>Hide Wins, Draws and Losses</strong>
                <small>Removes the record from player profiles for this team</small>
              </span>
            </label>
            <label className="toggle-row">
              <input
                type="checkbox"
                checked={hidePlayerRatings}
                onChange={(e) => setHidePlayerRatings(e.target.checked)}
              />
              <span>
                <strong>Hide skills hexagon</strong>
                <small>Hides the player ratings chart on player profiles</small>
              </span>
            </label>
          </fieldset>

          <fieldset className="settings-block">
            <legend>Shirt colour</legend>
            <p className="hint">
              Colour kit used on player profile, match details, and lineup (White kit stays white).
            </p>
            <div className="colour-controls">
              <label htmlFor="kit-colour-picker">Colour</label>
              <input
                id="kit-colour-picker"
                type="color"
                value={kitColour || DEFAULT_KIT_COLOUR}
                onChange={(e) => setKitColour(e.target.value)}
              />
              <label htmlFor="kit-colour-hex">Hex</label>
              <input
                id="kit-colour-hex"
                type="text"
                value={kitColour}
                onChange={(e) => setKitColour(e.target.value)}
                maxLength={7}
                placeholder="#9f1717"
              />
            </div>
          </fieldset>

          <button type="submit" className="save-button" disabled={saving}>
            <FaSave /> {saving ? 'Saving…' : 'Save settings'}
          </button>
        </form>
      )}
    </div>
  );
};

export default TeamSettings;
